import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiServiceProxy } from 'v2/services/api-proxy';

const EMBEDDED_BROWSER_PATTERN = /WhatsApp|Instagram|FBAN|FBAV|FB_IAB|Line\/|MicroMessenger|TikTok|Snapchat/i;
const FORCED_DOWNLOAD_TYPE = 'application/octet-stream';

export type DownloadFile = {
  id: number;
  name: string;
  extension: string;
  url_download: string;
};

const buildFileName = (file: DownloadFile) => {
  const extension = file.extension.replace(/^\./, '').trim();
  const name = file.name.trim() || 'arquivo';

  if (!extension) {
    return name;
  }

  if (name.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
    return name;
  }

  return `${name}.${extension}`;
};

const triggerAnchorDownload = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
};

const isIosEmbeddedBrowser = () => {
  const userAgent = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(userAgent);

  return isIos && EMBEDDED_BROWSER_PATTERN.test(userAgent);
};

const isShareAbort = (error: unknown) => {
  return error instanceof DOMException && error.name === 'AbortError';
};

const shareFile = async (file: File) => {
  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file] });
    return;
  }

  triggerAnchorDownload(file, file.name);
};

export const useDownload = (hash: string) => {
  const preparedFilesRef = useRef(new Map<number, File>());
  const [readyToSaveId, setReadyToSaveId] = useState<number | null>(null);
  const listDownloads = useQuery({
    queryKey: ['downloadFile', hash],
    queryFn: async () => {
      const result = await apiServiceProxy.get<DownloadFile[]>(`/downloadfile/${hash}`);
      return result.data;
    },
    enabled: !!hash
  });

  useEffect(() => {
    if (listDownloads.isError) {
      toast.error('Ops, erro na listagem de downloads');
    }
  }, [listDownloads.isError]);

  const validateDownloadUrl = async (url: string): Promise<boolean> => {
    const fileHash = url.split('/').pop();
    const result = await apiServiceProxy.post('/validatedownloadhash', { hash: fileHash });
    return result.data?.status;
  };

  const getDownloadUrl = async (file: DownloadFile): Promise<string | null> => {
    const isValid = await validateDownloadUrl(file.url_download);

    if (isValid) {
      return file.url_download;
    }

    const refreshedDownloads = await listDownloads.refetch();

    if (refreshedDownloads.isError) {
      return null;
    }

    const refreshedFile = refreshedDownloads.data?.find(item => item.id === file.id);

    if (!refreshedFile) {
      return null;
    }

    const refreshedIsValid = await validateDownloadUrl(refreshedFile.url_download);

    if (!refreshedIsValid) {
      return null;
    }

    return refreshedFile.url_download;
  };

  const downloadFile = useMutation({
    mutationFn: async (file: DownloadFile) => {
      const url = await getDownloadUrl(file);

      if (!url) {
        toast.error('Link de download inválido');
        return;
      }

      try {
        const response = await fetch(url);

        if (!response.ok) {
          toast.error('Não foi possível baixar o arquivo');
          return;
        }

        const blob = await response.blob();
        const preparedFile = new File([blob], buildFileName(file), { type: FORCED_DOWNLOAD_TYPE });

        if (!isIosEmbeddedBrowser()) {
          triggerAnchorDownload(preparedFile, preparedFile.name);
          return;
        }

        preparedFilesRef.current.set(file.id, preparedFile);
        setReadyToSaveId(file.id);

        try {
          await shareFile(preparedFile);
        } catch (error) {
          if (isShareAbort(error)) {
            return;
          }

          toast.info('Toque novamente para salvar o arquivo');
        }
      } catch {
        toast.error('Não foi possível baixar o arquivo');
      }
    }
  });

  const startDownload = (file: DownloadFile) => {
    const preparedFile = preparedFilesRef.current.get(file.id);

    if (preparedFile && isIosEmbeddedBrowser()) {
      return shareFile(preparedFile).catch(error => {
        if (isShareAbort(error)) {
          return;
        }

        triggerAnchorDownload(preparedFile, preparedFile.name);
      });
    }

    return downloadFile.mutateAsync(file);
  };

  return {
    downloadFile: startDownload,
    isDownloading: listDownloads.isFetching,
    downloadingUrl: downloadFile.isPending ? downloadFile.variables?.url_download : undefined,
    readyToSaveId,
    downloads: listDownloads.data
  };
};
